// frontend/src/pages/CamDoBrainObjectives.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import camdobrainService from '../services/camdobrainService';
import Loading from '../components/Loading';
import './CamDoBrainObjectives.css';

const CamDoBrainObjectives = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const brandProfileIdParam = searchParams.get('brand_profile_id');

  // State
  const [loading, setLoading] = useState(true);
  const [brandProfiles, setBrandProfiles] = useState([]);
  const [selectedBrandId, setSelectedBrandId] = useState(null);
  const [objectives, setObjectives] = useState([]);
  const [selectedObjective, setSelectedObjective] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [actionLoading, setActionLoading] = useState(null);
  const [tacticLoading, setTacticLoading] = useState(null);
  const [error, setError] = useState(null);

  // Load initial data
  useEffect(() => {
    loadInitialData();
  }, []);

  // Load objectives when brand changes
  useEffect(() => {
    if (selectedBrandId) {
      loadObjectives(selectedBrandId);
    }
  }, [selectedBrandId, statusFilter]);

  const loadInitialData = async () => {
    try {
      setLoading(true);
      const profilesRes = await camdobrainService.getBrandProfiles();
      const profiles = Array.isArray(profilesRes.data)
        ? profilesRes.data
        : (profilesRes.data?.results || []);
      setBrandProfiles(profiles);

      if (brandProfileIdParam && profiles.find(p => p.id === parseInt(brandProfileIdParam))) {
        setSelectedBrandId(parseInt(brandProfileIdParam));
      } else if (profiles.length > 0) {
        setSelectedBrandId(profiles[0].id);
      }
    } catch (err) {
      console.error('Error loading data:', err);
      setError('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  const loadObjectives = async (brandId) => {
    try {
      const status = statusFilter !== 'all' ? statusFilter : null;
      const res = await camdobrainService.getObjectives(brandId, status);
      const objs = Array.isArray(res.data)
        ? res.data
        : (res.data?.results || []);
      setObjectives(objs);
      setError(null);
    } catch (err) {
      console.error('Error loading objectives:', err);
      setObjectives([]);
    }
  };

  const handleActivate = async (id) => {
    try {
      setActionLoading(id);
      await camdobrainService.activateObjective(id);
      loadObjectives(selectedBrandId);
    } catch (err) {
      console.error('Error activating objective:', err);
      setError(err.response?.data?.error || 'Error al activar objetivo');
    } finally {
      setActionLoading(null);
    }
  };

  const handlePause = async (id) => {
    try {
      setActionLoading(id);
      await camdobrainService.pauseObjective(id);
      loadObjectives(selectedBrandId);
    } catch (err) {
      console.error('Error pausing objective:', err);
      setError(err.response?.data?.error || 'Error al pausar objetivo');
    } finally {
      setActionLoading(null);
    }
  };

  const handleComplete = async (id) => {
    try {
      setActionLoading(id);
      await camdobrainService.completeObjective(id);
      loadObjectives(selectedBrandId);
    } catch (err) {
      console.error('Error completing objective:', err);
      setError(err.response?.data?.error || 'Error al completar objetivo');
    } finally {
      setActionLoading(null);
    }
  };

  const handleTrackProgress = async (id) => {
    try {
      setActionLoading(id);
      await camdobrainService.trackObjectiveProgress(id);
      loadObjectives(selectedBrandId);
    } catch (err) {
      console.error('Error tracking progress:', err);
      setError(err.response?.data?.error || 'Error al actualizar progreso');
    } finally {
      setActionLoading(null);
    }
  };

  const handleTacticToggle = async (objectiveId, tacticIndex, currentCompleted) => {
    try {
      setTacticLoading(`${objectiveId}-${tacticIndex}`);
      await camdobrainService.updateTactic(objectiveId, tacticIndex, !currentCompleted);
      loadObjectives(selectedBrandId);
    } catch (err) {
      console.error('Error updating tactic:', err);
      setError(err.response?.data?.error || 'Error al actualizar tactica');
    } finally {
      setTacticLoading(null);
    }
  };

  const getPriorityColor = (priority) => {
    const colors = {
      'primary': '#8b5cf6',
      'secondary': '#3b82f6',
      'tertiary': '#64748b',
    };
    return colors[priority] || '#64748b';
  };

  const getStatusBadge = (status) => {
    const badges = {
      'recommended': { label: 'Recomendado', bg: '#fef3c7', color: '#92400e' },
      'active': { label: 'Activo', bg: '#dcfce7', color: '#166534' },
      'completed': { label: 'Completado', bg: '#dbeafe', color: '#1e40af' },
      'paused': { label: 'Pausado', bg: '#f1f5f9', color: '#475569' },
    };
    return badges[status] || { label: status, bg: '#f1f5f9', color: '#475569' };
  };

  const getObjectiveTypeIcon = (type) => {
    const icons = {
      'increase_reach': '📢',
      'improve_engagement': '💬',
      'grow_followers': '📈',
      'increase_saves': '💾',
      'improve_content_mix': '🎨',
      'boost_virality': '🚀',
      'community_building': '🤝',
    };
    return icons[type] || '🎯';
  };

  // Normalize tactics to always be objects with text and completed
  const normalizeTactics = (tactics) => {
    if (!tactics || !Array.isArray(tactics)) return [];
    return tactics.map(tactic => {
      if (typeof tactic === 'string') {
        return { text: tactic, completed: false };
      }
      return tactic;
    });
  };

  // Calculate tactics completion percentage
  const getTacticsProgress = (tactics) => {
    const normalized = normalizeTactics(tactics);
    if (normalized.length === 0) return 0;
    const completed = normalized.filter(t => t.completed).length;
    return Math.round((completed / normalized.length) * 100);
  };

  // Prepare progress history data for chart
  const getProgressChartData = (progressHistory) => {
    if (!progressHistory || !Array.isArray(progressHistory)) return [];

    // Filter only progress updates and format for chart
    return progressHistory
      .filter(entry => entry.progress_percentage !== undefined)
      .map(entry => ({
        date: new Date(entry.date).toLocaleDateString('es-CL', { day: '2-digit', month: 'short' }),
        progress: Math.round(entry.progress_percentage),
        event: entry.event,
      }))
      .slice(-10); // Last 10 entries
  };

  // Calculate KPI change from previous values
  const getKPIChange = (objective, kpiName) => {
    const trackings = objective.kpi_trackings || [];
    const kpiTrackings = trackings
      .filter(t => t.metric_name === kpiName)
      .sort((a, b) => new Date(b.recorded_at) - new Date(a.recorded_at));

    if (kpiTrackings.length < 2) return null;

    const current = kpiTrackings[0].metric_value;
    const previous = kpiTrackings[1].metric_value;
    const change = current - previous;
    const changePercent = previous !== 0 ? ((change / previous) * 100).toFixed(1) : 0;

    return {
      change,
      changePercent,
      isPositive: change > 0,
      isNeutral: change === 0,
    };
  };

  // Render progress history chart
  const renderProgressChart = (objective) => {
    const chartData = getProgressChartData(objective.progress_history);

    if (chartData.length < 2) {
      return (
        <div className="no-history-message">
          No hay suficiente historial de progreso todavia
        </div>
      );
    }

    return (
      <div className="chart-container">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
            <XAxis
              dataKey="date"
              tick={{ fontSize: 12, fill: '#64748b' }}
              axisLine={{ stroke: '#e2e8f0' }}
            />
            <YAxis
              domain={[0, 100]}
              tick={{ fontSize: 12, fill: '#64748b' }}
              axisLine={{ stroke: '#e2e8f0' }}
              tickFormatter={(value) => `${value}%`}
            />
            <Tooltip
              formatter={(value) => [`${value}%`, 'Progreso']}
              contentStyle={{
                background: 'white',
                border: '1px solid #e2e8f0',
                borderRadius: '8px',
                fontSize: '0.85rem'
              }}
            />
            <Line
              type="monotone"
              dataKey="progress"
              stroke="#8b5cf6"
              strokeWidth={2}
              dot={{ fill: '#8b5cf6', strokeWidth: 2, r: 4 }}
              activeDot={{ r: 6, fill: '#7c3aed' }}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
    );
  };

  // Render interactive tactics list
  const renderTacticsList = (objective) => {
    const tactics = normalizeTactics(objective.tactics);
    const progress = getTacticsProgress(objective.tactics);
    const completedCount = tactics.filter(t => t.completed).length;

    if (tactics.length === 0) {
      return <p className="no-history-message">No hay tacticas definidas</p>;
    }

    return (
      <>
        {tactics.map((tactic, index) => {
          const isLoading = tacticLoading === `${objective.id}-${index}`;
          return (
            <div
              key={index}
              className={`tactic-item ${tactic.completed ? 'completed' : ''} ${isLoading ? 'tactic-loading' : ''}`}
              onClick={() => handleTacticToggle(objective.id, index, tactic.completed)}
            >
              <div className="tactic-checkbox-interactive">
                {tactic.completed && '✓'}
              </div>
              <span className="tactic-text">{tactic.text}</span>
            </div>
          );
        })}
        <div className="tactics-progress-summary">
          <span>{completedCount}/{tactics.length} completadas</span>
          <div className="tactics-progress-bar">
            <div
              className="tactics-progress-fill"
              style={{ width: `${progress}%` }}
            />
          </div>
          <span>{progress}%</span>
        </div>
      </>
    );
  };

  // Render enhanced KPI cards with change indicators
  const renderKPICards = (objective) => {
    const kpis = objective.kpis || [];
    const latestKpis = objective.latest_kpis || {};

    if (kpis.length === 0) {
      return <p className="no-history-message">No hay KPIs definidos</p>;
    }

    return (
      <div className="kpis-grid">
        {kpis.map((kpi, index) => {
          const latestData = latestKpis[kpi.name];
          const change = getKPIChange(objective, kpi.name);
          const progress = latestData?.progress || 0;

          // Determine progress status
          let progressClass = 'on-track';
          if (progress < 30) progressClass = 'off-track';
          else if (progress < 70) progressClass = 'warning';

          return (
            <div key={index} className="kpi-card-enhanced">
              <div className="kpi-header">
                <div className="kpi-name-enhanced">{kpi.name}</div>
                {change && (
                  <div className={`kpi-change-indicator ${
                    change.isNeutral ? 'neutral' :
                    change.isPositive ? 'positive' : 'negative'
                  }`}>
                    {change.isPositive ? '↑' : change.isNeutral ? '→' : '↓'}
                    {Math.abs(change.changePercent)}%
                  </div>
                )}
              </div>
              <div className="kpi-values">
                {latestData ? (
                  <>
                    <span className="kpi-current-value">{latestData.value}</span>
                    <span className="kpi-unit">{latestData.unit}</span>
                  </>
                ) : (
                  <span className="kpi-current-value">--</span>
                )}
              </div>
              <div className="kpi-target-info">
                <span>Meta: {kpi.target}</span>
                {latestData && <span>• {Math.round(progress)}% alcanzado</span>}
              </div>
              {latestData && (
                <div className="kpi-mini-progress">
                  <div className="kpi-mini-bar">
                    <div
                      className={`kpi-mini-fill ${progressClass}`}
                      style={{ width: `${Math.min(progress, 100)}%` }}
                    />
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  const primaryObjective = objectives.find(o => o.priority === 'primary');
  const otherObjectives = objectives.filter(o => o.priority !== 'primary');

  if (loading) {
    return <Loading />;
  }

  return (
    <div className="objectives-page">
      {/* Header */}
      <div className="objectives-header">
        <div className="header-left">
          <button className="back-btn" onClick={() => navigate('/camdobrain')}>
            ← Volver
          </button>
          <h1>
            <span className="icon">🎯</span>
            Objetivos Estrategicos
          </h1>
        </div>
        <div className="header-actions">
          <select
            value={selectedBrandId || ''}
            onChange={(e) => setSelectedBrandId(Number(e.target.value))}
            className="brand-select"
          >
            {brandProfiles.map(brand => (
              <option key={brand.id} value={brand.id}>
                {brand.brand_name}
              </option>
            ))}
          </select>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="status-filter"
          >
            <option value="all">Todos los estados</option>
            <option value="recommended">Recomendados</option>
            <option value="active">Activos</option>
            <option value="completed">Completados</option>
            <option value="paused">Pausados</option>
          </select>
          <button
            className="new-diagnostic-btn"
            onClick={() => navigate(`/camdobrain/diagnostic?brand_profile_id=${selectedBrandId}`)}
          >
            🔬 Nuevo Diagnostico
          </button>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="error-banner">
          {error}
          <button onClick={() => setError(null)}>×</button>
        </div>
      )}

      {/* No Objectives */}
      {objectives.length === 0 && (
        <div className="no-objectives">
          <div className="icon">🎯</div>
          <h2>Sin objetivos</h2>
          <p>Ejecuta un diagnostico para generar objetivos estrategicos personalizados.</p>
          <button onClick={() => navigate(`/camdobrain/diagnostic?brand_profile_id=${selectedBrandId}`)}>
            Ir a Diagnostico
          </button>
        </div>
      )}

      {/* Primary Objective */}
      {primaryObjective && (
        <div className="primary-objective-section">
          <h2>Objetivo Principal</h2>
          <div className="primary-objective-card">
            <div className="objective-header">
              <div className="objective-icon">
                {getObjectiveTypeIcon(primaryObjective.objective_type)}
              </div>
              <div className="objective-title-area">
                <h3>{primaryObjective.title}</h3>
                <span className="objective-type">{primaryObjective.objective_type_display}</span>
              </div>
              <div
                className="status-badge"
                style={{
                  background: getStatusBadge(primaryObjective.status).bg,
                  color: getStatusBadge(primaryObjective.status).color
                }}
              >
                {getStatusBadge(primaryObjective.status).label}
              </div>
            </div>

            <p className="objective-description">{primaryObjective.description}</p>

            {/* Progress */}
            <div className="progress-section">
              <div className="progress-header">
                <span>Progreso</span>
                <span>{Math.round(primaryObjective.progress_percentage)}%</span>
              </div>
              <div className="progress-bar">
                <div
                  className="progress-fill"
                  style={{ width: `${primaryObjective.progress_percentage}%` }}
                ></div>
              </div>
            </div>

            {/* Progress History Chart */}
            {primaryObjective.status === 'active' && (
              <div className="progress-chart-section">
                <h4>Historial de Progreso</h4>
                {renderProgressChart(primaryObjective)}
              </div>
            )}

            {/* Tactics */}
            {primaryObjective.tactics?.length > 0 && (
              <div className="tactics-section">
                <h4>Tacticas</h4>
                {renderTacticsList(primaryObjective)}
              </div>
            )}

            {/* KPIs */}
            {primaryObjective.kpis?.length > 0 && (
              <div className="kpis-section">
                <h4>KPIs</h4>
                {renderKPICards(primaryObjective)}
              </div>
            )}

            {/* Actions */}
            <div className="objective-actions">
              {primaryObjective.status === 'recommended' && (
                <button
                  className="action-btn activate"
                  onClick={() => handleActivate(primaryObjective.id)}
                  disabled={actionLoading === primaryObjective.id}
                >
                  {actionLoading === primaryObjective.id ? '...' : '▶️ Activar'}
                </button>
              )}
              {primaryObjective.status === 'active' && (
                <>
                  <button
                    className="action-btn track"
                    onClick={() => handleTrackProgress(primaryObjective.id)}
                    disabled={actionLoading === primaryObjective.id}
                  >
                    {actionLoading === primaryObjective.id ? '...' : '📊 Actualizar Progreso'}
                  </button>
                  <button
                    className="action-btn pause"
                    onClick={() => handlePause(primaryObjective.id)}
                    disabled={actionLoading === primaryObjective.id}
                  >
                    ⏸️ Pausar
                  </button>
                  <button
                    className="action-btn complete"
                    onClick={() => handleComplete(primaryObjective.id)}
                    disabled={actionLoading === primaryObjective.id}
                  >
                    ✅ Completar
                  </button>
                </>
              )}
              {primaryObjective.status === 'paused' && (
                <button
                  className="action-btn activate"
                  onClick={() => handleActivate(primaryObjective.id)}
                  disabled={actionLoading === primaryObjective.id}
                >
                  {actionLoading === primaryObjective.id ? '...' : '▶️ Reanudar'}
                </button>
              )}
            </div>

            {/* Meta info */}
            <div className="objective-meta">
              <span>Duracion estimada: {primaryObjective.estimated_duration_weeks} semanas</span>
              <span>Confianza IA: {primaryObjective.ai_confidence}%</span>
            </div>
          </div>
        </div>
      )}

      {/* Other Objectives */}
      {otherObjectives.length > 0 && (
        <div className="other-objectives-section">
          <h2>Otros Objetivos</h2>
          <div className="objectives-grid">
            {otherObjectives.map(objective => (
              <div
                key={objective.id}
                className={`objective-card ${selectedObjective === objective.id ? 'expanded' : ''}`}
                onClick={() => setSelectedObjective(
                  selectedObjective === objective.id ? null : objective.id
                )}
              >
                <div className="card-header">
                  <div className="objective-icon-small">
                    {getObjectiveTypeIcon(objective.objective_type)}
                  </div>
                  <div className="card-title-area">
                    <h4>{objective.title}</h4>
                    <span
                      className="priority-badge"
                      style={{ background: getPriorityColor(objective.priority) }}
                    >
                      {objective.priority_display}
                    </span>
                  </div>
                  <div
                    className="status-badge-small"
                    style={{
                      background: getStatusBadge(objective.status).bg,
                      color: getStatusBadge(objective.status).color
                    }}
                  >
                    {getStatusBadge(objective.status).label}
                  </div>
                </div>

                {/* Progress bar mini */}
                <div className="progress-mini">
                  <div
                    className="progress-fill-mini"
                    style={{ width: `${objective.progress_percentage}%` }}
                  ></div>
                </div>

                {/* Expanded content */}
                {selectedObjective === objective.id && (
                  <div className="card-expanded" onClick={(e) => e.stopPropagation()}>
                    <p className="objective-description">{objective.description}</p>

                    {objective.tactics?.length > 0 && (
                      <div className="tactics-section">
                        <h4>Tacticas</h4>
                        {renderTacticsList(objective)}
                      </div>
                    )}

                    <div className="card-actions">
                      {objective.status === 'recommended' && (
                        <button
                          onClick={() => handleActivate(objective.id)}
                          disabled={actionLoading === objective.id}
                        >
                          {actionLoading === objective.id ? '...' : 'Activar'}
                        </button>
                      )}
                      {objective.status === 'active' && (
                        <>
                          <button
                            onClick={() => handleTrackProgress(objective.id)}
                            disabled={actionLoading === objective.id}
                          >
                            Actualizar
                          </button>
                          <button
                            onClick={() => handleComplete(objective.id)}
                            disabled={actionLoading === objective.id}
                          >
                            Completar
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default CamDoBrainObjectives;
